import Logo from '@assets/logos/swise_white_on_dark.svg';
import List from '@mui/material/List';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  CancelIcon,
  ContentContainer,
  HeaderMobile,
  Offset,
  StyledDrawer,
  StyledLogo,
} from './Menu.styles';

import { useMediaQueries } from '@utils/mediaQueries/mediaQueries';
import { Menu as MenuIcon } from '@assets/svg';
import { useIntl } from 'react-intl';
import { useEffect, useState } from 'react';
import { Box, IconButton } from '@mui/material';
import { Flex, LogoutConfirmation, MenuItem } from '@components';
import { getMenuItems } from './Menu.consts';
import { useIntercom } from 'react-use-intercom';
import WithUser, { WithUserProps } from '@HOCs/withUser';

const drawerWidth = 240;

const Menu: React.FC<WithUserProps> = ({ user }) => {
  const intl = useIntl();
  const { sm: isMobile } = useMediaQueries();
  const navigate = useNavigate();
  const location = useLocation();
  const paths = location.pathname.split('/');
  const tabValue = location.pathname === '/' ? null : paths[1];
  const { boot } = useIntercom();
  const [logoutConfirmationOpen, setLogoutConfirmationOpen] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const menuItems = getMenuItems(navigate, () =>
    setLogoutConfirmationOpen(true)
  ).filter((x) => x.userTypes.includes(user.userType));

  const menuItemMatch =
    menuItems.find(
      (x) => x.id === tabValue || x.matchingRoutes?.includes(tabValue ?? '')
    )?.id ?? menuItems[0].id;

  const [selectedMenuItem, setSelectedMenuItem] = useState(menuItemMatch);
  useEffect(() => {
    setSelectedMenuItem(menuItemMatch);
  }, [menuItemMatch, menuItems, tabValue]);

  const handleCloseLogoutConfirmation = () => setLogoutConfirmationOpen(false);

  const handleDrawerToggle = () => {
    setIsMobileOpen(!isMobileOpen);
  };

  const handleMenuItemClick = (id: string, itemOnClick: () => void) => {
    setSelectedMenuItem(id);
    itemOnClick();
    setIsMobileOpen(!isMobileOpen);
  };

  const handleLogoClick = (): void => {
    navigate('/');
  };

  useEffect(() => {
    boot({
      name: `${user.basicDetails.firstName} ${user.basicDetails.lastName}`,
      email: user.email,
      userId: user.email,
    });
  }, [boot, user]);

  const drawer = (
    <div>
      <Flex flexDirection='column' marginX={12} marginY={16}>
        <Flex flex={1} justifyContent='space-between' alignItems='center'>
          <StyledLogo src={Logo} onClick={handleLogoClick} />
          <Flex sx={{ display: { sm: 'none' } }}>
            <CancelIcon onClick={handleDrawerToggle} />
          </Flex>
        </Flex>
      </Flex>
      <List sx={{ marginTop: 8 }}>
        {menuItems.map(({ id, Icon, onClick, onlyMobile }) => (
          <MenuItem
            key={id}
            data-testid={`menu-item-${id}`}
            Icon={Icon}
            label={intl.formatMessage({ id: `menu.${id}` })}
            onClick={() => handleMenuItemClick(id, onClick)}
            active={id === selectedMenuItem}
            sx={{ display: { sm: onlyMobile ? 'none' : 'flex' } }}
          />
        ))}
      </List>
    </div>
  );

  return (
    <Flex height='100%' overflow='hidden'>
      {isMobile ? (
        <HeaderMobile position='fixed'>
          <StyledLogo src={Logo} onClick={handleLogoClick} />
          <IconButton
            color='inherit'
            aria-label='open drawer'
            edge='start'
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
        </HeaderMobile>
      ) : null}
      <Box
        component='nav'
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <StyledDrawer
          variant='temporary'
          open={isMobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </StyledDrawer>
        <StyledDrawer
          variant='permanent'
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
          open
        >
          {drawer}
        </StyledDrawer>
      </Box>

      <Box
        flexGrow={1}
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)`, xs: '100%' },
          height: isMobile ? 'calc(100% - 72px)' : '100%',
        }}
      >
        {isMobile ? <Offset /> : null}
        <ContentContainer>
          <Outlet />
        </ContentContainer>
      </Box>
      <LogoutConfirmation
        isOpen={logoutConfirmationOpen}
        onClose={handleCloseLogoutConfirmation}
      />
    </Flex>
  );
};

export default WithUser(Menu);
